/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-nested-ternary */
import { FC, useEffect, useState, ChangeEvent, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X, AlertTriangle } from 'react-feather';
import styles from './EditPriceDrawer.module.css';
import { EditPriceDrawerProps } from './EditPriceDrawerProps.types';
import Tabs from '@/components/Tabs/Tabs';
import { t } from 'i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import { ProductsIncluded } from './Internal/EditPriceTabItems';
import { TabProductsExcluded } from './Internal/TabProductsExcluded';
import { useRemoveProduct, useUpdateProductPrice, useVariants } from '@/services/menu/menus.service';
import { useSTKContext } from '@/context/context';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import {
  getStoreGroups,
  getStores,
  isPriceInvalid,
  checkIfHasVariants,
  getUniqStores,
  combineStoreGroupPrices,
} from '@/services/menu/menus.helper.service';
import { InputStates } from '@/components/Input/Input.types';
import { Switch, Tooltip, Typography } from '@mui/material';
import Button from '@/components/Button/Button';
import { ProductRemoveRequest, Variant, VariantPriceUpdateRequest } from '@/types/menus.types';
import CurrentPrice from './EditPriceTabItems/CurrentPrice';
import _, { set } from 'lodash';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import { tooltipProps } from '../StoreMenuDetails/internal/TooltipHelper';
import { TooltipContent } from '../StoreMenuDetails/internal/TooltipHelper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

function buildVariantPriceRequest(variants: Variant[], inputPrice: string): VariantPriceUpdateRequest[] {
  return variants.map((variant) => ({
    stores: variants.filter((sp1) => sp1.variantId === variant.variantId).flatMap((sp) => sp.variantStores),
    variantId: variant.variantId,
    price: inputPrice.replace('$', ''),
  }));
}

const ProductsIncludedTabIcon = (hasMandatoryLock: boolean, hasPriceLock: boolean) => {
  if (hasMandatoryLock && hasPriceLock) {
    return (
      <>
        <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.mandatoryProductsMsg')} />}>
          <AlertTriangle size={18} color="#E6740A" />
        </Tooltip>
        <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.lockedPriceMsg')} />}>
          <LockOutlinedIcon
            style={{ color: '#E6740A' }}
            data-testid="locked-icon"
            sx={{
              width: '18px',
              height: '18px',
            }}
          />
        </Tooltip>
      </>
    );
  } else if (hasMandatoryLock) {
    return (
      <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.mandatoryProductsMsg')} />}>
        <AlertTriangle size={18} color="#E6740A" />
      </Tooltip>
    );
  } else if (hasPriceLock) {
    return (
      <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.lockedPriceMsg')} />}>
        <LockOutlinedIcon
          style={{ color: '#E6740A' }}
          data-testid="locked-icon"
          sx={{
            width: '18px',
            height: '18px',
          }}
        />
      </Tooltip>
    );
  } else {
    return null;
  }
};

export const EditPriceDrawer: FC<EditPriceDrawerProps> = ({
  isOpen,
  onClose,
  storeProducts,
  afterSave,
  setPriceDeviationsData,
}) => {
  const extractedVariantIds = storeProducts
    .filter((prod) => !(prod.priceLock && prod.mandatoryProductLock))
    .filter((sp) => !!sp.variantId)
    .map((sp) => sp.variantId); // I'll need to pass this to Price to the PriceDeviationsDrawer
  const { storesAndGroups } = useSTKContext();
  const [state, setState] = useState(isOpen);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [productIsRemoved, setProductIsRemoved] = useState(false);
  const [inputPriceState, setInputPriceState] = useState(InputStates.Default);
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const { alias } = useFranchiseeAlias();
  const { data: variants = [] } = useVariants(true, stores, storeGroups, extractedVariantIds as string[]);
  const {
    mutateAsync: updateProductPrice,
    data: updatePriceResponse,
    isLoading: isSavingPrice = false,
  } = useUpdateProductPrice();
  const {
    mutateAsync: removeProduct,
    data: removeProductResponse,
    isLoading: isRemovingProduct = false,
  } = useRemoveProduct();

  const [activeTab, setActiveTab] = useState(0);
  const [inputPrice, setInputPrice] = useState('');
  const [active, setActive] = useState(true);
  const hasMandatoryLock = useMemo(
    () => storeProducts.some((prod) => prod.mandatoryProductLock === true),
    [storeProducts],
  );
  const hasPriceLock = useMemo(() => storeProducts.some((prod) => prod.priceLock === true), [storeProducts]);
  const allProductsMandatory = useMemo(
    () => storeProducts.every((prod) => prod.mandatoryProductLock === true),
    [storeProducts],
  );
  const allProductsHavePriceLock = useMemo(
    () => storeProducts.every((prod) => prod.priceLock === true),
    [storeProducts],
  );
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(0);
      setInputPrice('');
    }
    setState(isOpen);
  }, [isOpen]);

  useEffect(() => {}, [variants]);

  const handleTabClick = (indexOfClickedTab: number) => {
    setActiveTab(indexOfClickedTab);
  };

  const currentStoreProducts = storeProducts;
  const data = combineStoreGroupPrices(currentStoreProducts, storesAndGroups);
  const [tableData, setTableData] = useState(data);
  const sortedTableData = tableData.sort((a, b) => a.storeGroupName.localeCompare(b.storeGroupName));
  const minValidPrices: number[] = [];
  const storeNames = tableData.map((store) => store.storeGroupName);

  currentStoreProducts.forEach((sProduct) => {
    sProduct.storeGroups.forEach((sGroup) => {
      if (storeNames.includes(sGroup.storeGroupName) && storeNames.length > 0) {
        minValidPrices.push(sGroup?.minValidPrice);
      }
    });
  });

  const maxValidPrices: number[] = [];

  currentStoreProducts.forEach((sProduct) => {
    sProduct.storeGroups.forEach((sGroup) => {
      if (storeNames.includes(sGroup.storeGroupName) && storeNames.length > 0) {
        maxValidPrices.push(sGroup?.maxValidPrice);
      }
    });
  });

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputPrice(value);
  };
  const lowestValidPrice = minValidPrices.length !== 0 ? Math.min(...minValidPrices).toFixed(2) : '';
  const highestValidPrice = maxValidPrices.length !== 0 ? Math.max(...maxValidPrices).toFixed(2) : '';

  const processProduct = () => {
    if (!active) {
      // Get variants without mandatoryProductLock
      const variantsWithNoMandatoryLock = variants.filter((variant) => !variant.mandatoryProductLock);
      // Get their IDs
      const variantIdsWithNoMandatoryLock = variantsWithNoMandatoryLock.map((variant) => variant.variantId);
      // Collect all unique store IDs from these variants
      const storeIdsSet = new Set<string>();
      variantsWithNoMandatoryLock.forEach((variant) => {
        if (variant.variantStores && Array.isArray(variant.variantStores)) {
          variant.variantStores.forEach((storeId) => {
            storeIdsSet.add(storeId);
          });
        }
      });
      const stores = Array.from(storeIdsSet);

      const productRemoveRequest: ProductRemoveRequest = {
        variantIds: variantIdsWithNoMandatoryLock,
        stores,
      };
      removeProduct(productRemoveRequest, {
        onSuccess: () => {
          setProductIsRemoved(true);
          afterSave();
        },
      });
    } else if (alias === null && isPriceInvalid(inputPrice, lowestValidPrice, highestValidPrice)) {
      setInputPriceState(InputStates.Error);
    } else {
      const variantsWithNoPriceLock = variants.filter((variant) => !variant.priceLock);
      const variantPriceUpdateRequest = buildVariantPriceRequest(variantsWithNoPriceLock, inputPrice);

      updateProductPrice(variantPriceUpdateRequest, {
        onSuccess: () => {
          setPriceIsSaved(true);
          afterSave();
        },
        onError: () => {
          // next task to be called when we have some error
        },
      });
    }
  };

  const onCloseDrawer = () => () => {
    setPriceIsSaved(false);
    setProductIsRemoved(false);
    setInputPriceState(InputStates.Default);
    setInputPrice('');
    setState(false);
    onClose();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return (
          <ProductsIncluded
            storeProducts={currentStoreProducts.filter((prod) => !(prod.priceLock && prod.mandatoryProductLock))}
          />
        );

      case 2:
        return <TabProductsExcluded variants={variants} />;

      default:
        return (
          <CurrentPrice
            tableData={sortedTableData}
            setPriceDeviationsData={setPriceDeviationsData}
            storeProducts={storeProducts}
            deactivateProduct={!active}
          />
        );
    }
  };

  const generateTabs = () => {
    const hasExcludedStores = checkIfHasVariants(variants);

    const tabs = [
      { label: t('editMenuPrice.currentPrice'), id: 'currentPrice' },
      { label: t('editMenuPrice.productsIncluded'), id: 'productsIncluded' },
    ];

    if (hasExcludedStores) {
      tabs.push({
        label: t('editMenuPrice.productsExcluded'),
        id: 'productsExcluded',
      });
    }

    return tabs;
  };
  const uniqueStores = useMemo(() => getUniqStores(variants), [variants]);

  const toggleActive = () => {
    if (active) {
      setInputPrice('');
      setInputPriceState(InputStates.Default);
    }
    setActive(!active);
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer()} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('editMenuPrice.editProducts')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer()}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.content}>
            {isSavingPrice || isRemovingProduct ? (
              <EditDrawerProcessing count={uniqueStores.length} entityToUpdate={EntityToUpdate.MENU} />
            ) : priceIsSaved || productIsRemoved ? (
              <EditDrawerSuccessAndFailure
                apiResponse={!active ? removeProductResponse : updatePriceResponse}
                entityToUpdate={EntityToUpdate.MENU}
              />
            ) : (
              <>
                <div className={styles.productSettings}>
                  <strong>{t('editMenuPrice.productSettings')}</strong>
                  <p className={styles.subtext1}>{t('editMenuPrice.productSettingsSubtext')}</p>
                  <div className={styles.switchContainer}>
                    <Switch
                      color="error"
                      title="switch"
                      checked={active}
                      onChange={toggleActive}
                      disabled={allProductsMandatory}
                    />
                    <div className={styles.switchLabel}>{active ? 'Active' : 'Inactive'}</div>
                    {hasMandatoryLock && (
                      <Tooltip
                        {...tooltipProps}
                        title={<TooltipContent text={t('menuDetails.mandatoryProductsMsg')} />}
                      >
                        <AlertTriangle size={19} color="#E6740A" className={styles.alertTriangle} />
                      </Tooltip>
                    )}
                  </div>
                  {!active && <div className={styles.subtext2}>{t('editMenuPrice.inactiveSubtext')}</div>}
                  <MaskedInput
                    className={styles.input}
                    id="new-price"
                    label={
                      hasPriceLock ? (
                        <>
                          <span>{t('editMenuPrice.newPricePoint')}</span>
                          <Tooltip {...tooltipProps} title={<TooltipContent text={t('menuDetails.lockedPriceMsg')} />}>
                            <LockOutlinedIcon
                              style={{ color: '#E6740A' }}
                              data-testid="locked-icon"
                              sx={{
                                width: '22px',
                                height: '22px',
                                marginLeft: '4px',
                                marginBottom: '-4px',
                              }}
                            />
                          </Tooltip>
                        </>
                      ) : (
                        t('editMenuPrice.newPricePoint')
                      )
                    }
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    value={inputPrice}
                    disabled={!active || allProductsHavePriceLock}
                    onChange={handlePriceChange}
                    state={inputPriceState}
                  />
                  <Typography
                    mt={'2px'}
                    mb={'10px'}
                    sx={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: `${inputPriceState === InputStates.Error ? 'var(--red-700)' : 'var(--black-700)'}`,
                    }}
                  >
                    {t('editMenuPrice.priceRange')}: ${lowestValidPrice} - ${highestValidPrice}
                  </Typography>
                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.submitButton}
                      data-testid="edit-price"
                      disabled={tableData.length === 0 || (active && inputPrice === '')}
                      onClick={() => {
                        processProduct();
                      }}
                      type="button"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <Tabs
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                    tabs={generateTabs().map((tab) => ({
                      ...tab,
                      ...(tab.id === 'productsIncluded' && {
                        icon: ProductsIncludedTabIcon(hasMandatoryLock, hasPriceLock),
                      }),
                      ...(tab.id === 'productsExcluded' && { icon: <AlertTriangle size="16px" color="red" /> }),
                    }))}
                  />
                  <div className={styles.tabContainer}>{renderTabContent()}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
